import { ConfigProvider, Modal, notification, Skeleton, Spin } from "antd";
import { usePayment } from "config";
import { closePaymentModal } from "flutterwave-react-v3";
import useUserLoans from "hooks/useUserLoans";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { verifyRepayment } from "services/LoanService";

function StatusCard({ status = "PENDING" }) {
  const statusMap = {
    PENDING: "Pending",
    REPAID: "Repaid",
    PARTIAL_REPAYMENT: "Partial Repayment",
    OVERDUE: "Overdue",
  };

  const styleMap = {
    PENDING:
      "inline-block px-2 lg:px-4 text-xs border border-solid border-gray-200 font-medium rounded-lg py-2 text-gray-600 bg-gray-100",
    OVERDUE:
      "inline-block px-2 lg:px-4 text-xs border border-solid border-red-200 font-medium rounded-lg py-2 text-red-400 bg-red-100",
    REPAID:
      "inline-block px-2 lg:px-4 text-xs border border-solid border-green-200 font-medium rounded-lg py-2 text-green-600 bg-green-100",
    PARTIAL_REPAYMENT:
      "inline-block px-2 lg:px-4 text-xs border border-solid border-gray-200 font-medium rounded-lg py-2 text-gray-500 bg-gray-100",
  };

  if (!statusMap[status]) return null;

  return <span className={styleMap[status]}>{statusMap[status]}</span>;
}

function LoanCard({ loan, refetch = () => {} }) {
  const [repaymentAmount, setRepaymentAmount] = useState(
    loan?.amount?.toString()
  );
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("0px");
  const paymentMethod = "paystack";
  const [isPaid, setIsPaid] = useState(false);
  const [transRes, setTransRes] = useState(null);

  const dueDate = useMemo(() => {
    const jsDate = new Date(loan.institution.graduation_year);
    return DateTime.fromJSDate(jsDate)
      .plus({ year: 1 })
      .toFormat("dd LLL, yyyy");
  }, [loan]);

  const formatDate = (date) => {
    if (!date) return "";
    return DateTime.fromJSDate(new Date(date)).toFormat("dd LLL, yyyy");
  };

  useEffect(() => {
    setRepaymentAmount(loan?.due_amount);
  }, [loan]);

  useEffect(() => {
    if (selectedType?.value === "FULL_REPAYMENT") {
      setRepaymentAmount(loan?.due_amount);
    }
  }, [selectedType, loan]);

  useEffect(() => {
    if (contentRef.current) {
      setMaxHeight(open ? `${contentRef.current.scrollHeight}px` : "0px");
    }
  }, [open]);

  const handleRepaymentVerification = useCallback(
    async ({
      isPaid,
      transRes,
      loan,
      refetch,
      amount,
      paymentMethod = "paystack",
    }) => {
      const failed = !isPaid || !transRes;

      // ⚠️ Non-Paystack methods: currently unsupported
      if (paymentMethod !== "paystack") {
        if (failed) {
          return notification.error({
            message: "Payment Failed",
            description: "Sorry, could not complete payment.",
          });
        }
        return;
      }

      // ⚠️ Paystack failed payment
      if (failed) {
        return notification.error({
          message: "Payment Failed",
          description: "Sorry, could not complete payment.",
        });
      }

      // 🧾 Extract reference info for backend
      const { tx_ref: reference, transaction_id } = transRes;

      // ⚙️ Prepare payload for backend
      const payload = {
        id: loan.id,
        paymentMethod,
        amount,
        reference,
        transaction_id,
        ...transRes,
      };

      // 🔍 Verify repayment with backend
      const res = await verifyRepayment(payload);

      if (res.status === "success") {
        refetch();
        closePaymentModal();
        return;
      }

      notification.error({
        message: "An Error Occurred",
        description:
          res.message ?? "An error occurred while verifying repayment.",
      });
    },
    []
  );

  const { loading, setLoading, startPayment } = usePayment({
    paymentMethod,
    userProfile: loan.applicant,
    amount: repaymentAmount,
    callback: (response) => {
      setLoading(false);

      const successStatuses = ["successful", "success", "completed"];
      const isSuccessful = successStatuses.includes(response?.status);

      if (isSuccessful) {
        setIsPaid(true);
        setTransRes(response);

        handleRepaymentVerification({
          isPaid: true,
          transRes: response,
          loan,
          refetch,
          amount: repaymentAmount,
        });
      } else {
        notification.error({
          message: "Payment Failed",
          description: "Sorry, your payment could not be processed.",
        });
      }

      closePaymentModal();
    },
  });

  const onContinue = () => {
    const due = loan?.due_amount;
    const fraction = due * 0.1;

    if (selectedType?.value === "FULL_REPAYMENT" && repaymentAmount !== due) {
      return notification.error({
        message: "Error",
        description: "With Full Repayment amount should be the same due amount",
      });
    }

    if (repaymentAmount > due) {
      return notification.error({
        message: "Error",
        description: "Repayment amount should not exceed due amount",
      });
    }

    if (repaymentAmount < fraction) {
      return notification.error({
        message: "Error",
        description: `Partial Repayment amount should at least be 10% of due amount (₦${fraction.toLocaleString()})`,
      });
    }

    if (paymentMethod !== "paystack") {
      if (isPaid && transRes) {
        handleRepaymentVerification({
          isPaid,
          transRes,
          loan,
          refetch,
          amount: repaymentAmount,
        });
        return;
      }
    }

    if (isPaid) {
      handleRepaymentVerification({
        isPaid,
        transRes,
        loan,
        refetch,
        amount: repaymentAmount,
      });
      return;
    }

    startPayment();
  };

  return (
    <>
      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        centered
        footer={null}
      >
        <div className="w-full">
          <p className="text-lg lg:text-2xl font-poppins font-medium">
            Repay Loan
          </p>

          <div className="my-6 space-y-6">
            {/* Repayment Type Dropdown */}
            <div>
              <p className="text-xs lg:text-sm font-poppins text-primary-700 mb-2">
                Repayment Type
              </p>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full border border-solid border-primary-200 bg-white text-primary-900 
                       py-3 px-3 rounded-lg text-left font-poppins flex items-center justify-between"
                >
                  {selectedType ? selectedType.label : "Select repayment type"}
                  <span className="text-primary-600">▼</span>
                </button>

                {dropdownOpen && (
                  <ul className="absolute mt-1 w-full bg-white border border-primary-200 rounded-lg shadow-md z-20">
                    {[
                      { label: "Full Repayment", value: "FULL_REPAYMENT" },
                      {
                        label: "Partial Repayment",
                        value: "PARTIAL_REPAYMENT",
                      },
                    ].map((item) => (
                      <li
                        key={item.value}
                        onClick={() => {
                          setSelectedType(item);
                          setDropdownOpen(false);
                        }}
                        className="p-3 cursor-pointer hover:bg-primary-50 font-poppins text-sm"
                      >
                        {item.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Amount */}
            <div className="w-full">
              <p className="text-xs lg:text-sm font-poppins text-primary-700 mb-2">
                Amount (₦)
              </p>
              <input
                className="text-primary-900 p-3 border w-full rounded-lg border-solid border-primary-200 
                     font-poppins disabled:opacity-60 disabled:cursor-not-allowed"
                type="number"
                disabled={selectedType?.value === "FULL_REPAYMENT"}
                value={repaymentAmount}
                onChange={(e) => setRepaymentAmount(e.target.value)}
              />
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-y-3 sm:gap-x-3 sm:justify-end pt-3">
              <button
                disabled={loading}
                onClick={onContinue}
                className="bg-primary flex items-center justify-center disabled:opacity-65 border border-solid text-white rounded-lg px-6 py-3 text-base font-poppins w-full sm:w-auto"
              >
                {loading ? (
                  <ConfigProvider
                    theme={{
                      components: { Spin: { colorPrimary: "#ffffff" } },
                    }}
                  >
                    <Spin />
                  </ConfigProvider>
                ) : (
                  <span>Continue</span>
                )}
              </button>
              <button
                disabled={loading}
                className="bg-red-400 text-white disabled:opacity-65 rounded-lg px-6 py-3 text-base font-poppins w-full sm:w-auto"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <li className="bg-white rounded-lg shadow">
        <header className="p-4 relative border-b border-solid border-primary-100">
          <div className="lg:flex space-y-2 lg:space-y-0 items-start ">
            <div className="mb-2">
              <div className="text-right absolute right-4 lg:hidden">
                <StatusCard status={loan?.repayment_status ?? "PENDING"} />
              </div>
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Amount Due
              </p>
              <p className="text-lg lg:text-2xl font-poppins text-primary font-medium">
                ₦{loan?.due_amount?.toLocaleString()}
              </p>
              <div className="flex items-center gap-x-1">
                <p className="text-xs lg:text-sm font-poppins text-gray-600">
                  Amount Paid:{" "}
                </p>
                <p className="text-xs lg:text-sm font-poppins text-primary font-medium">
                  ₦{loan?.total_amount_paid?.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="lg:ml-auto mb-4 lg:mb-0 lg:text-right">
              {loan.repayment_status !== "REPAID" && (
                <button
                  onClick={() => setModalOpen(true)}
                  className="hidden lg:inline-block bg-primary border border-solid text-white rounded-lg px-4 py-2 text-xs lg:text-sm"
                >
                  Repay Loan
                </button>
              )}
              {
                loan.repayment_status !== "REPAID"
                ?(

              <div className="mt-1 mb-4 lg:mb-0 flex items-center gap-x-1">
                <p className="text-xs lg:text-sm font-poppins text-gray-600">
                  Due on:
                </p>
                <p className="text-xs lg:text-sm font-poppins text-primary font-medium">
                  {formatDate(dueDate)}
                </p>
              </div>
                )
                :(

              <div className="mt-1 mb-4 lg:mb-0 items-center gap-x-1">
                <p className="text-xs lg:text-sm font-poppins text-gray-600">
                  Repaid at
                </p>
                <p className="text-xs lg:text-sm font-poppins text-primary font-medium">
                  {formatDate(loan.latest_repayment.timestamp)}
                </p>
              </div>
                )
              }
            </div>
          </div>

          <div className="flex items-center gap-x-4">
            <StatusCard status={loan?.repayment_status ?? "PENDING"} />
            {loan.repayment_status !== "REPAID" && (
              <button
                onClick={() => setModalOpen(true)}
                className="lg:hidden bg-primary border border-solid text-white rounded-lg px-4 py-2 text-xs lg:text-sm"
              >
                Repay Loan
              </button>
            )}

            <button
              className="bg-primary-100 ml-auto font-medium border border-solid border-primary-300 text-primary-600 rounded-lg px-4 py-2 text-xs"
              onClick={() => setOpen(!open)}
            >
              {open ? "Hide Details" : "See Details"}
            </button>
          </div>
        </header>

        {/* Collapsible */}
        <div
          ref={contentRef}
          style={{ maxHeight }}
          className="overflow-hidden transition-all duration-300 ease-in-out"
        >
          <div className="p-4 lg:px-8 grid gap-4 lg:grid-cols-4">
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Application Date:
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                {formatDate(loan?.created_at)}
              </p>
            </div>
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Date Disbursed:
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                {formatDate(loan?.disburse_date)}
              </p>
            </div>
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Last Payment Date:
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                {loan?.latest_repayment
                  ? formatDate(loan.latest_repayment.timestamp)
                  : "N/A"}
              </p>
            </div>
            <div className="gap-x-1">
              <p className="text-xs lg:text-sm font-poppins text-gray-600">
                Total Loan Amount
              </p>
              <p className="font-medium text-xs lg:text-sm text-gray-800 font-poppins">
                ₦{loan?.amount?.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </li>
    </>
  );
}

function Loader() {
  return (
    <section className="space-y-4 mt-4">
      {Array(4)
        .fill("")
        .map(() => (
          <div className="shadow bg-white flex justify-between p-4 rounded-lg">
            <div className="flex-1 space-y-2">
              <div>
                <Skeleton.Button style={{ height: "14px" }} active />
              </div>
              <div className="max-w-[200px]">
                <Skeleton.Button block style={{ height: "34px" }} active />
              </div>
              <div className="max-w-[200px]">
                <Skeleton.Button block style={{ height: "14px" }} active />
              </div>
            </div>
            <div className="flex-1 text-right space-y-2">
              <div className="max-w-[200px] ml-auto">
                <Skeleton.Button block style={{ height: "24px" }} active />
              </div>
              <div>
                <Skeleton.Button style={{ height: "14px" }} active />
              </div>
              <div className="max-w-[150px] ml-auto">
                <Skeleton.Button block style={{ height: "16px" }} active />
              </div>
            </div>
          </div>
        ))}
    </section>
  );
}

function EmptyRepayments() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
      <svg
        className="w-16 h-16 mb-4 text-gray-300"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 17v-6a2 2 0 012-2h2a2 2 0 012 2v6m-6 4h6m-6 0a2 2 0 01-2-2m8 2a2 2 0 002-2"
        />
      </svg>
      <p className="text-sm sm:text-base font-poppins">
        No disbursed loans available.
      </p>
    </div>
  );
}

export function RepaymentLoans() {
  const { userLoans, loading, getLoans } = useUserLoans({
    status: "disbursed",
  });

  return (
    <section>
      <header className="text-center sm:text-left mb-4 sm:mb-0">
        <p className="font-poppins text-base sm:text-xl font-semibold text-secondary">
          Disbursed Loans ({userLoans.length})
        </p>
        <p className="font-poppins text-xs sm:text-sm text-gray-500">
          Loans with upcoming or current repayment due dates.
        </p>
      </header>

      {loading ? (
        <Loader />
      ) : userLoans.length === 0 ? (
        <EmptyRepayments />
      ) : (
        <ul className="space-y-4 my-4">
          {userLoans.map((item) => (
            <LoanCard refetch={getLoans} key={item.id} loan={item} />
          ))}
        </ul>
      )}
    </section>
  );
}
